import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// WeightedYesNoQuestion
//
// Three radio buttons: Yes / No / Partially.
// Design: §6.5 Question Type → WEIGHTED_YES_NO
// ─────────────────────────────────────────────

interface WeightedYesNoQuestionProps {
  questionId: string;
  value: string | null;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

// Labels resolved through i18n (common.yes/partially/no) at render time.
// Stored values are canonical schema option keys: "yes" / "partial" / "no"
// (see seed schemas and scoring.engine, which also tolerates legacy
// "partially"). The radio previously used "partially", so stored "partial"
// answers rendered as unanswered.
const OPTIONS = [
  { value: "yes", labelKey: "common.yes" },
  { value: "partial", labelKey: "common.partially" },
  { value: "no", labelKey: "common.no" },
] as const;

// Legacy rows may still hold "partially" — display them as "partial".
function normalizeValue(value: string | null): string | null {
  return value === "partially" ? "partial" : value;
}

export function WeightedYesNoQuestion({
  questionId,
  value,
  onChange,
  readOnly,
}: WeightedYesNoQuestionProps) {
  const { t } = useTranslation();
  return (
    <RadioGroup
      value={normalizeValue(value) ?? ""}
      onValueChange={readOnly ? undefined : onChange}
      className="flex flex-row gap-6"
    >
      {OPTIONS.map((opt) => (
        <div key={opt.value} className="flex items-center gap-2">
          <RadioGroupItem
            value={opt.value}
            id={`${questionId}-${opt.value}`}
            disabled={readOnly}
          />
          <label
            htmlFor={`${questionId}-${opt.value}`}
            className="cursor-pointer text-[14px] font-medium text-text-secondary"
          >
            {t(opt.labelKey)}
          </label>
        </div>
      ))}
    </RadioGroup>
  );
}
